import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Plus, Search, Edit2, Trash2, Settings, Building2, Store, Warehouse, RefreshCw, Download } from 'lucide-react';
import { expensesAPI } from '../../services/api';

const SCOPES = [
  { value: 'all', label: 'All (Common)', color: 'bg-blue-100 text-blue-700' },
  { value: 'hq', label: 'HQ Only', color: 'bg-purple-100 text-purple-700' },
  { value: 'store', label: 'Store Only', color: 'bg-green-100 text-green-700' },
  { value: 'warehouse', label: 'Warehouse Only', color: 'bg-orange-100 text-orange-700' }
];

const RECURRENCE_TYPES = [
  { value: 'none', label: 'None (One-time)' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

export default function ExpenseHeads() {
  const { toast } = useToast();
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingHead, setEditingHead] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    scope: 'all',
    is_recurring_default: false,
    default_recurrence: 'none',
    is_active: true
  });

  useEffect(() => {
    loadHeads();
  }, []);

  const loadHeads = async () => {
    try {
      setLoading(true);
      const data = await expensesAPI.listHeads();
      setHeads(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load expense heads', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const result = await expensesAPI.seedDefaultHeads();
      toast({ title: 'Success', description: result.message });
      loadHeads();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to seed default expense heads', variant: 'destructive' });
    }
  };

  const handleOpenDialog = (head = null) => {
    if (head) {
      setEditingHead(head);
      setFormData({
        name: head.name || '',
        code: head.code || '',
        description: head.description || '',
        scope: head.scope || 'all',
        is_recurring_default: head.is_recurring_default || false,
        default_recurrence: head.default_recurrence || 'none',
        is_active: head.is_active !== false
      });
    } else {
      setEditingHead(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        scope: 'all',
        is_recurring_default: false,
        default_recurrence: 'none',
        is_active: true
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast({ title: 'Error', description: 'Name and Code are required', variant: 'destructive' });
      return;
    }

    try {
      if (editingHead) {
        await expensesAPI.updateHead(editingHead.id, formData);
        toast({ title: 'Success', description: 'Expense head updated' });
      } else {
        await expensesAPI.createHead(formData);
        toast({ title: 'Success', description: 'Expense head created' });
      }
      setShowDialog(false);
      loadHeads();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (head) => {
    if (!window.confirm(`Are you sure you want to deactivate "${head.name}"?`)) return;
    try {
      await expensesAPI.deleteHead(head.id);
      toast({ title: 'Success', description: 'Expense head deactivated' });
      loadHeads();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to deactivate expense head', variant: 'destructive' });
    }
  };

  const getScopeIcon = (scope) => {
    switch (scope) {
      case 'hq': return <Building2 className="w-4 h-4" />;
      case 'store': return <Store className="w-4 h-4" />;
      case 'warehouse': return <Warehouse className="w-4 h-4" />;
      default: return <Settings className="w-4 h-4" />;
    }
  };

  const getScopeConfig = (scope) => {
    return SCOPES.find(s => s.value === scope) || SCOPES[0];
  };

  const filteredHeads = heads.filter(h => {
    const matchesSearch = h.name.toLowerCase().includes(search.toLowerCase()) ||
                         h.code.toLowerCase().includes(search.toLowerCase());
    const matchesScope = activeTab === 'all' ? true : h.scope === activeTab;
    return matchesSearch && matchesScope;
  });

  const countByScope = (scope) => {
    if (scope === 'all') return heads.length;
    return heads.filter(h => h.scope === scope).length;
  };

  return (
    <div className="p-6 space-y-6" data-testid="expense-heads-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Expense Categories</h1>
          <p className="text-gray-500">Manage expense heads for HQ, Stores, and Warehouses</p>
        </div>
        <div className="flex gap-2">
          {heads.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults} data-testid="seed-defaults-btn">
              <Download className="w-4 h-4 mr-2" /> Load Defaults
            </Button>
          )}
          <Button onClick={() => handleOpenDialog()} data-testid="add-head-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Category
          </Button>
        </div>
      </div>

      {/* Scope Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="all" className="gap-2">
              <Settings className="w-4 h-4" /> All ({countByScope('all')})
            </TabsTrigger>
            <TabsTrigger value="hq" className="gap-2">
              <Building2 className="w-4 h-4" /> HQ ({countByScope('hq')})
            </TabsTrigger>
            <TabsTrigger value="store" className="gap-2">
              <Store className="w-4 h-4" /> Store ({countByScope('store')})
            </TabsTrigger>
            <TabsTrigger value="warehouse" className="gap-2">
              <Warehouse className="w-4 h-4" /> Warehouse ({countByScope('warehouse')})
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-heads"
            />
          </div>
        </div>

        {/* Content */}
        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredHeads.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Settings className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No expense categories found</p>
                {heads.length === 0 && (
                  <Button variant="outline" className="mt-4" onClick={handleSeedDefaults}>
                    <Download className="w-4 h-4 mr-2" /> Load Default Categories
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredHeads.map((head) => {
                const scopeConfig = getScopeConfig(head.scope);
                return (
                  <Card key={head.id} className={`${!head.is_active ? 'opacity-60' : ''}`} data-testid={`head-card-${head.code}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${scopeConfig.color.split(' ')[0]}`}>
                            {getScopeIcon(head.scope)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{head.name}</h3>
                              <Badge variant="outline" className="text-xs">{head.code}</Badge>
                            </div>
                            {head.description && (
                              <p className="text-sm text-gray-500">{head.description}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <Badge className={scopeConfig.color}>
                            {scopeConfig.label}
                          </Badge>

                          {head.is_recurring_default && (
                            <Badge variant="outline" className="gap-1">
                              <RefreshCw className="w-3 h-3" />
                              {head.default_recurrence}
                            </Badge>
                          )}

                          <Badge variant={head.is_active ? 'default' : 'secondary'}>
                            {head.is_active ? 'Active' : 'Inactive'}
                          </Badge>

                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(head)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(head)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingHead ? 'Edit Category' : 'Add Expense Category'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Electricity"
                data-testid="head-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., ELEC"
                data-testid="head-code-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Applicable To</Label>
              <Select value={formData.scope} onValueChange={(v) => setFormData({ ...formData, scope: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCOPES.map((scope) => (
                    <SelectItem key={scope.value} value={scope.value}>{scope.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_recurring_default}
                onCheckedChange={(v) => setFormData({ ...formData, is_recurring_default: v })}
              />
              <Label>Default as Recurring Expense</Label>
            </div>

            {formData.is_recurring_default && (
              <div className="space-y-2">
                <Label>Default Recurrence</Label>
                <Select value={formData.default_recurrence} onValueChange={(v) => setFormData({ ...formData, default_recurrence: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-head-btn">
              {editingHead ? 'Update' : 'Add Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
